// Pipecat Cloud (Daily WebRTC voice / WebSocket chat) field group for the
// interface editor drawer (agent.md interfaceFields split). Rendered only
// when GET /v1/me connection_capabilities lists provider "pipecat_cloud".
import { docUrl } from "../../../lib/docLinks";
import { HelpTip, Stack, TextInput, Textarea } from "../../../ui";
import { IntegrationCredentialSelect } from "../../IntegrationCredentialSelect";
import type { InterfaceFieldsProps } from "../lib/connectionDraft";

export function PipecatCloudInterfaceFields({
  draft,
  onDraftChange,
}: InterfaceFieldsProps) {
  const isVoice = draft.interfaceKind === "pipecat_voice";
  return (
    <Stack gap={4}>
      <IntegrationCredentialSelect
        provider="pipecat_cloud"
        value={draft.pipecatApiKeyRef}
        onChange={(apiKeyRef) => {
          onDraftChange({ ...draft, pipecatApiKeyRef: apiKeyRef });
        }}
      />
      <TextInput
        label="Pipecat Cloud agent name"
        required
        value={draft.pipecatAgentName}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            pipecatAgentName: event.currentTarget.value,
          });
        }}
      />
      <Textarea
        help={
          <HelpTip
            docHref={docUrl("guides/connect-pipecat-cloud")}
            docLabel="Connect a Pipecat Cloud agent"
            label="About the initialization body"
          >
            {`Optional JSON object sent to the agent when the session starts. Maximum ${isVoice ? "1 MB" : "4 KB"}. Keep it secret-free: use the credential above for anything sensitive.`}
          </HelpTip>
        }
        label="Initialization body"
        rows={5}
        value={draft.pipecatBody}
        onChange={(event) => {
          onDraftChange({ ...draft, pipecatBody: event.currentTarget.value });
        }}
      />
      {!isVoice ? (
        <>
          <Textarea
            help={
              <HelpTip label="About the outbound pattern">
                {
                  "Enter a JSON object. Use {{text}} for the simulated persona message."
                }
              </HelpTip>
            }
            label="Outbound pattern"
            required
            rows={4}
            value={draft.pipecatOutboundPattern}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatOutboundPattern: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Inbound text path"
            required
            value={draft.pipecatInboundTextPath}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatInboundTextPath: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip label="About the inbound type field">
                Optional. Set this together with the allowed type values to
                filter incoming messages.
              </HelpTip>
            }
            label="Inbound type field"
            value={draft.pipecatInboundTypeField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatInboundTypeField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip label="About inbound type values">
                Optional comma-separated list of allowed message types.
              </HelpTip>
            }
            label="Inbound type values"
            value={draft.pipecatInboundTypeValues}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatInboundTypeValues: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip
                docHref={docUrl("guides/connect-pipecat-cloud")}
                docLabel="Connect a Pipecat Cloud agent"
                label="About the echo filter"
              >
                {
                  "Inbound messages whose `Echo-filter field` equals `Echo-filter value` are dropped, so text your agent reflects back never enters the Transcript."
                }
              </HelpTip>
            }
            label="Echo-filter field"
            value={draft.pipecatEchoField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatEchoField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Echo-filter value"
            value={draft.pipecatEchoEquals}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatEchoEquals: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Agent-end field"
            required
            value={draft.pipecatAgentEndField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatAgentEndField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Agent-end value"
            required
            value={draft.pipecatAgentEndEquals}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                pipecatAgentEndEquals: event.currentTarget.value,
              });
            }}
          />
        </>
      ) : null}
    </Stack>
  );
}
